interface CognitoAuthResponse {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
  TokenType?: string;
  ExpiresIn?: number;
}

interface CognitoAuthError {
  __type: string;
  message: string;
}

interface CognitoSignUpResponse {
  Session?: string;
  UserConfirmed?: boolean;
  UserSub?: string;
}

interface CognitoInitiateAuthResponse {
  AvailableChallenges?: string[];
  ChallengeName?: string;
  ChallengeParameters?: {
    CODE_DELIVERY_DELIVERY_MEDIUM?: string;
    CODE_DELIVERY_DESTINATION?: string;
  };
  Session?: string;
}

interface CognitoRespondToChallengeResponse {
  AuthenticationResult?: CognitoAuthResponse;
  ChallengeParameters?: Record<string, unknown>;
  ChallengeName?: string;
  Session?: string;
}

class CognitoAuthService {
  private readonly clientId: string;
  private readonly cognitoEndpoint: string;

  constructor(clientId: string) {
    this.clientId = clientId;
    this.cognitoEndpoint =
      process.env.REACT_APP_AWS_COGNITO_ENDPOINT ??
      'https://cognito-idp.eu-west-2.amazonaws.com/';
  }

  private async makeRequest<T>(
    target: string,
    data: Record<string, unknown>,
  ): Promise<T> {
    const response = await fetch(this.cognitoEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': target,
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(JSON.stringify(result as CognitoAuthError));
    }

    return result as T;
  }

  async signUp(email: string): Promise<CognitoSignUpResponse> {
    try {
      return await this.makeRequest<CognitoSignUpResponse>(
        'AWSCognitoIdentityProviderService.SignUp',
        {
          ClientId: this.clientId,
          Username: email,
          UserAttributes: [
            {
              Name: 'email',
              Value: email,
            },
          ],
        },
      );
    } catch (error) {
      // If user already exists, we can continue to login
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      try {
        const parsedError = JSON.parse(errorMessage) as CognitoAuthError;
        if (
          parsedError.__type === 'UsernameExistsException' ||
          parsedError.__type === 'NotAuthorizedException'
        ) {
          // User already exists or SignUp is not permitted - continue to login
          return { UserConfirmed: true };
        }
      } catch {
        // Ignore parsing errors
      }
      throw error;
    }
  }

  async initiateAuth(email: string): Promise<CognitoInitiateAuthResponse> {
    try {
      return await this.makeRequest<CognitoInitiateAuthResponse>(
        'AWSCognitoIdentityProviderService.InitiateAuth',
        {
          AuthFlow: 'USER_AUTH',
          ClientId: this.clientId,
          AuthParameters: {
            USERNAME: email,
          },
        },
      );
    } catch (error) {
      // Handle Cognito-specific errors and provide user-friendly messages
      if (error instanceof Error) {
        const parsedError = JSON.parse(error.message) as CognitoAuthError;

        if (parsedError.__type === 'UserNotFoundException') {
          throw new Error(
            'This email address is not registered. Please contact your administrator to get access.',
          );
        } else if (parsedError.__type === 'NotAuthorizedException') {
          throw new Error(
            'Authentication failed. Please check your credentials or contact your administrator.',
          );
        } else if (parsedError.__type === 'UserNotConfirmedException') {
          throw new Error(
            'Your account has not been confirmed. Please contact your administrator.',
          );
        } else if (parsedError.__type === 'InvalidParameterException') {
          throw new Error(
            'Invalid request. Please check your email format and try again.',
          );
        } else if (parsedError.__type === 'LimitExceededException') {
          throw new Error(
            'Too many requests. Please wait a moment and try again.',
          );
        } else if (parsedError.__type === 'TooManyRequestsException') {
          throw new Error(
            'Too many requests. Please wait before trying again.',
          );
        } else if (parsedError.message) {
          throw new Error(`Authentication error: ${parsedError.message}`);
        }
      } else {
        throw new Error(
          'Authentication failed. Please try again or contact your administrator.',
        );
      }

      // Fallback for unknown errors
      throw new Error(
        'Authentication failed. Please try again or contact your administrator.',
      );
    }
  }

  async respondToAuthChallenge(
    email: string,
    otpCode: string,
    session: string,
  ): Promise<CognitoRespondToChallengeResponse> {
    return await this.makeRequest<CognitoRespondToChallengeResponse>(
      'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
      {
        ChallengeName: 'EMAIL_OTP',
        ClientId: this.clientId,
        ChallengeResponses: {
          USERNAME: email,
          EMAIL_OTP_CODE: otpCode,
        },
        Session: session,
      },
    );
  }

  async refreshToken(refreshToken: string): Promise<CognitoAuthResponse> {
    return await this.makeRequest<CognitoAuthResponse>(
      'AWSCognitoIdentityProviderService.InitiateAuth',
      {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: this.clientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      },
    );
  }

  // Parse JWT token to get user info
  parseJwtToken(token: string): Record<string, unknown> | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload) as Record<string, unknown>;
    } catch (error) {
      // Error parsing JWT token
      return null;
    }
  }
}

export default CognitoAuthService;
export type {
  CognitoAuthResponse,
  CognitoAuthError,
  CognitoSignUpResponse,
  CognitoInitiateAuthResponse,
  CognitoRespondToChallengeResponse,
};
